import {HubConnectionState} from '@microsoft/signalr'
import {getInterpreterHubConnection} from './interpreterHubConnection'

export type AgentNetworkProbePayload = {
  latencyMs: number
  success: boolean
  endpoint: string
  error?: string | null
}

export type AgentWebRtcSamplePayload = {
  atMs: number
  direction: 'input' | 'output'
  packetsLost?: number
  packetsCount?: number
  audioLevel?: number
  jitterMs?: number
  rttMs?: number
}

export type AgentWebRtcBatchRequest = {
  contactId?: string | null
  samples: AgentWebRtcSamplePayload[]
}

export type AgentWebRtcCallReportRequest = {
  contactId?: string | null
  atMs: number
  iceConnectionsLost?: number
  iceConnectionsFailed?: number
  peerConnectionFailed?: boolean
  consecutiveNoAudioOutputPackets?: number
  consecutiveNoAudioInputPackets?: number
  consecutiveLowOutputAudioLevel?: number
  consecutiveLowInputAudioLevel?: number
  consecutiveAudioOutputMuteDurationS?: number
  talkingTimeMs?: number
  firstRtpTimeMs?: number
  iceCollectionFailure?: boolean
  signallingConnectionFailure?: boolean
  handshakingFailure?: boolean
  isMediaClusterPath?: boolean
  userAgent?: string
}

export type AgentCcpEventName =
  | 'softphone_error'
  | 'ccp_ws_lost'
  | 'ccp_ws_restored'
  | 'mute'
  | 'unmute'
  | 'hold'
  | 'resume'
  | 'contact_connected'
  | 'contact_ended'
  | 'audio_device_change'
  | 'tab_hidden'
  | 'tab_visible'

export type AgentCcpEventRequest = {
  event: AgentCcpEventName
  atMs: number
  contactId?: string | null
  detail?: string | null
}

export type AgentNetworkPingRequest = {
  clientSentAtMs: number
  connectInstance?: AgentNetworkProbePayload | null
  connectRegion?: AgentNetworkProbePayload | null
  status: string
  contactId?: string | null
  /** document.visibilityState, normalized to visible or hidden. */
  visibility: 'visible' | 'hidden'
  /** Ms since the last agent.onRefresh. Omitted until the first snapshot. */
  ccpSnapshotAgeMs?: number | null
  /** Agent softphone mute. Omitted until the CCP has reported it. */
  muted?: boolean | null
}

/**
 * Send a sample on the existing InterpreterHub connection.
 * Uses send (no reply). Returns false when the hub is down so the caller skips the tick.
 */
export async function sendAgentNetworkPing(payload: AgentNetworkPingRequest): Promise<boolean> {
  const connection = getInterpreterHubConnection()
  if (!connection || connection.state !== HubConnectionState.Connected) return false
  await connection.send('ReportNetworkPing', payload)
  return true
}

/** One CCP softphone-metrics batch (about 30s of one-second samples). */
export async function sendAgentWebRtcBatch(payload: AgentWebRtcBatchRequest): Promise<boolean> {
  const connection = getInterpreterHubConnection()
  if (!connection || connection.state !== HubConnectionState.Connected) return false
  if (!payload.samples.length) return false
  await connection.send('ReportWebRtcHealth', payload)
  return true
}

export async function sendAgentWebRtcCallReport(
  payload: AgentWebRtcCallReportRequest,
): Promise<boolean> {
  const connection = getInterpreterHubConnection()
  if (!connection || connection.state !== HubConnectionState.Connected) return false
  await connection.send('ReportWebRtcCallReport', payload)
  return true
}

export async function sendAgentCcpEvent(payload: AgentCcpEventRequest): Promise<boolean> {
  const connection = getInterpreterHubConnection()
  if (!connection || connection.state !== HubConnectionState.Connected) return false
  await connection.send('ReportCcpEvent', payload)
  return true
}
